import { Box, CircularProgress } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { gql, useMutation, useQuery } from "@outschool/ui-apollo";
import { BlockScopedMessageInput } from "@outschool/ui-components-classroom";
import React, { useCallback, useEffect, useMemo, useRef } from "react";

import {
  BlockScopedThreadDetailQuery,
  BlockScopedThreadDetailQueryVariables,
} from "../../generated/graphql";
import { MARK_THREAD_AS_READ_MUTATION } from "../../mutations/MarkThreadAsRead";
import { BLOCK_SCOPED_THREAD_DETAIL_QUERY } from "../../queries/BlockScopedThreadDetailQuery";
import { MESSAGE_TEACHER_POLL_INTERVAL_MS } from "./constants";
import DateSeparator from "./DateSeparator";
import MessageBubble from "./MessageBubble";
import ThreadViewLayout from "./ThreadViewLayout";
import type {
  BlockScopedPostTarget,
  MessageData,
  ThreadViewContext,
} from "./types";
import { learnerSenderFromLearner, postTargetFromThread } from "./utils";

function isSameGroup(a: MessageData, b: MessageData): boolean {
  return (
    a.sender.uid === b.sender.uid &&
    dayjs(a.sentAt).format("YYYY-MM-DD HH:mm") ===
      dayjs(b.sentAt).format("YYYY-MM-DD HH:mm")
  );
}

interface ThreadDetailViewProps {
  threadUid: string;
  viewContext: ThreadViewContext;
  postTitle?: string | null;
  searchQuery?: string | null;
  optimisticMessages?: MessageData[];
  isTemporaryThread?: boolean;
  temporaryPostTarget?: BlockScopedPostTarget;
  onBootstrapMessagesHydrated?: (threadUid: string) => void;
  onBack: () => void;
  onClose: () => void;
}

export default function ThreadDetailView({
  threadUid,
  viewContext,
  postTitle: postTitleProp,
  searchQuery,
  optimisticMessages = [],
  isTemporaryThread = false,
  temporaryPostTarget,
  onBootstrapMessagesHydrated,
  onBack,
  onClose,
}: ThreadDetailViewProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\ThreadDetailView",
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [markAsRead] = useMutation(MARK_THREAD_AS_READ_MUTATION, {
    update(cache) {
      const threadCacheId = cache.identify({
        __typename: "BlockScopedThread",
        uid: threadUid,
      });
      const prev = cache.readFragment<{ unreadCount: number }>({
        id: threadCacheId,
        fragment: gql`
          fragment ThreadUnread on BlockScopedThread {
            unreadCount
          }
        `,
      });
      const previousUnread = prev?.unreadCount ?? 0;
      cache.modify({
        id: threadCacheId,
        fields: { unreadCount: () => 0 },
      });
      const sectionCacheId = cache.identify({
        __typename: "Section",
        uid: viewContext.section.uid,
      });
      cache.modify({
        id: sectionCacheId,
        fields: {
          unreadBlockScopedThreadsCount(existing: number = 0) {
            return Math.max(0, existing - previousUnread);
          },
        },
      });
    },
  });

  const { data, loading, refetch } = useQuery<
    BlockScopedThreadDetailQuery,
    BlockScopedThreadDetailQueryVariables
  >(BLOCK_SCOPED_THREAD_DETAIL_QUERY, {
    variables: { threadUid },
    skip: isTemporaryThread,
    fetchPolicy: "cache-and-network",
    pollInterval: MESSAGE_TEACHER_POLL_INTERVAL_MS,
    onCompleted: () => {
      markAsRead({ variables: { threadUid } });
    },
  });

  const thread = data?.blockScopedThread;
  const threadMessages = useMemo(
    () => thread?.messages ?? [],
    [thread?.messages],
  );
  const messages = useMemo(
    () => (threadMessages.length > 0 ? threadMessages : optimisticMessages),
    [threadMessages, optimisticMessages],
  );
  const hydratedThreadUidRef = useRef<string | null>(null);

  useEffect(() => {
    if (threadUid !== hydratedThreadUidRef.current) {
      hydratedThreadUidRef.current = null;
    }
  }, [threadUid]);

  useEffect(() => {
    if (
      isTemporaryThread ||
      !onBootstrapMessagesHydrated ||
      optimisticMessages.length === 0 ||
      threadMessages.length === 0 ||
      hydratedThreadUidRef.current === threadUid
    ) {
      return;
    }
    hydratedThreadUidRef.current = threadUid;
    onBootstrapMessagesHydrated(threadUid);
  }, [
    isTemporaryThread,
    onBootstrapMessagesHydrated,
    optimisticMessages.length,
    threadMessages.length,
    threadUid,
  ]);

  const title =
    postTitleProp ??
    thread?.classPost?.title ??
    thread?.learningAssignmentClassPost?.title ??
    "";
  const composerPostTarget = useMemo(
    () => postTargetFromThread(thread) ?? temporaryPostTarget ?? null,
    [thread, temporaryPostTarget],
  );

  const matchingUids = useMemo(() => {
    if (!searchQuery) {
      return new Set<string>();
    }
    const lower = searchQuery.toLowerCase();
    return new Set(
      messages
        .filter(m => m.messageContent.text?.toLowerCase().includes(lower))
        .map(m => m.uid),
    );
  }, [messages, searchQuery]);

  const firstMatchUid = useMemo(() => {
    return messages.find(m => matchingUids.has(m.uid))?.uid ?? null;
  }, [messages, matchingUids]);

  const firstMatchScrolled = useRef(false);

  useEffect(() => {
    firstMatchScrolled.current = false;
  }, [threadUid]);

  const firstMatchRef = useCallback((node: HTMLDivElement | null) => {
    if (node && !firstMatchScrolled.current) {
      firstMatchScrolled.current = true;
      node.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    if (firstMatchUid) {
      return;
    }
    const container = scrollContainerRef.current;
    if (!container || messages.length === 0) {
      return;
    }

    const scrollToBottom = () => {
      container.scrollTop = container.scrollHeight;
    };

    scrollToBottom();
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    // Re-scroll when child elements resize (e.g., images finishing load),
    const resizeObserver = new ResizeObserver(scrollToBottom);
    for (const child of Array.from(container.children)) {
      resizeObserver.observe(child);
    }

    return () => resizeObserver.disconnect();
  }, [messages.length, firstMatchUid]);

  return (
    <ThreadViewLayout
      section={viewContext.section}
      leader={viewContext.leader}
      postTitle={title}
      onBack={onBack}
      onClose={onClose}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 12,
        }}
      >
        {!isTemporaryThread && loading && messages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          messages.map((message, index) => {
            const prev = messages[index - 1];
            const next = messages[index + 1];
            const showDateSeparator =
              !prev ||
              dayjs(message.sentAt).format("YYYY-MM-DD") !==
                dayjs(prev.sentAt).format("YYYY-MM-DD");
            const isFirstInGroup =
              showDateSeparator || !prev || !isSameGroup(prev, message);
            const isLastInGroup = !next || !isSameGroup(message, next);
            const isMatch = matchingUids.has(message.uid);
            const isFirstMatch = message.uid === firstMatchUid;

            return (
              <React.Fragment key={message.uid}>
                {showDateSeparator && (
                  <DateSeparator
                    date={dayjs(message.sentAt).format("MMMM D, YYYY")}
                  />
                )}
                <MessageBubble
                  ref={isFirstMatch ? firstMatchRef : undefined}
                  message={message}
                  teacher={viewContext.leader}
                  learnerAvatar={viewContext.learner.avatar}
                  showTimestamp={isFirstInGroup}
                  showAvatar={isFirstInGroup}
                  isLastInGroup={isLastInGroup}
                  highlighted={isMatch}
                />
              </React.Fragment>
            );
          })
        )}
      </Box>

      {composerPostTarget && (
        <BlockScopedMessageInput
          placeholder={t("Message your teacher")}
          sectionUid={viewContext.section.uid}
          learnerUid={viewContext.learner.uid}
          threadUid={isTemporaryThread ? undefined : threadUid}
          {...composerPostTarget}
          sender={learnerSenderFromLearner(viewContext.learner)}
          disabled={isTemporaryThread}
          onMessageSent={isTemporaryThread ? undefined : () => refetch()}
          sx={{ borderColor: "divider" }}
        />
      )}
    </ThreadViewLayout>
  );
}
